/**
 * ForumSubNav — sticky category quick-switcher that appears on every forum
 * page (Community, Category, Topic, Search). Users can jump between any
 * category in one tap without hunting through breadcrumbs.
 *
 * Includes: All-Community anchor · scrollable colored category pills ·
 * inline search input · New Topic CTA (kept out of every page's header).
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Input } from '../ui/input';
import { Search, Home, Plus, ShieldCheck } from 'lucide-react';

const ForumSubNav = ({ activeCategorySlug = null, onNewTopic }) => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/forum/categories`);
        setCategories(r.data.categories || []);
      } catch { setCategories([]); }
    })();
  }, [API]);

  const displayCategories = useMemo(
    () => categories.filter((c) => !c.is_lesson_bridge),
    [categories]
  );

  const submitSearch = (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    navigate(`/community/search?q=${encodeURIComponent(query.trim())}`);
  };

  const homeActive = location.pathname === '/community' && !activeCategorySlug;

  return (
    <div className="sticky top-14 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 py-2">
          {/* All-community anchor */}
          <Link
            to="/community"
            data-testid="subnav-home"
            className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold transition-colors flex-shrink-0
              ${homeActive ? 'bg-foreground text-background' : 'bg-card border border-border text-foreground/75 hover:text-foreground hover:border-foreground/60'}`}
          >
            <Home className="w-3.5 h-3.5" /> All
          </Link>

          {/* Category pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar min-w-0" data-testid="subnav-categories">
            {displayCategories.map((c) => {
              const active = activeCategorySlug === c.slug;
              return (
                <Link
                  key={c.id}
                  to={`/community/c/${c.slug}`}
                  data-testid={`subnav-cat-${c.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold transition-colors flex-shrink-0"
                  style={active
                    ? { backgroundColor: c.color, color: 'white' }
                    : undefined}
                >
                  {!active && (
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c.color }} />
                  )}
                  <span className={active ? '' : 'text-foreground/75'}>{c.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Search + CTA */}
          <form onSubmit={submitSearch} className="ml-auto flex items-center gap-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search community…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="subnav-search-input"
                className="h-8 pl-8 w-40 sm:w-56 text-sm"
              />
            </div>
            {onNewTopic && (
              <button
                type="button"
                onClick={onNewTopic}
                data-testid="subnav-new-topic"
                className="hidden sm:inline-flex items-center gap-1 rounded-full h-8 px-3 text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> New
              </button>
            )}
            <Link
              to="/community-standards"
              data-testid="subnav-community-standards"
              title="Community Standards"
              className="hidden sm:inline-flex items-center gap-1 rounded-full h-8 px-2.5 text-xs font-bold border border-border text-muted-foreground hover:text-foreground hover:border-foreground/60 transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Rules
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForumSubNav;
